import { Flex } from "@chakra-ui/react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { usePapaParse } from "react-papaparse";
import { graphql, useFragment } from "react-relay";

import { MonthlyReportTable_site$key } from "~/src/__relay_artifacts__/MonthlyReportTable_site.graphql";
import { Table } from "~/src/components/common/tables/Table";

import { CSVDownloadLink } from "../CSVDownloadLink";
import { DisableColumnSelectButton } from "../DisableColumnSelectButton";
import {
  ColumnData,
  ReportTableDataRow,
  ReportTableHeaderRow,
} from "../ReportTable";
import { RowHeightSettingButton } from "../RowHeightSettingButton";
import { disableColumnOptions, headerOptionsForMonthlyReport } from "../utils";

const fragment = graphql`
  fragment MonthlyReportTable_site on Site {
    reportMonthly(input: $input)
  }
`;

export type Prop = {
  onSortClick: (params: any) => void;
  siteRef: MonthlyReportTable_site$key;
};

export const MonthlyReportTable: FC<
  React.PropsWithChildren<React.PropsWithChildren<Prop>>
> = ({ siteRef, onSortClick }) => {
  const { reportMonthly } = useFragment<typeof siteRef>(fragment, siteRef);
  const [disableColumns, setDisableColumns] = useState<number[]>([]);
  const [rowHeight, setRowHeight] = useState<number>(56);

  const { readString } = usePapaParse();

  const [data, setData] = useState<{ headers: string[]; rows: string[][] }>({
    headers: [],
    rows: [],
  });

  useEffect(() => {
    readString(reportMonthly.summary_report, {
      worker: true,
      complete: (results: any) => {
        const [headers, ...rest] = results.data;
        setData({ headers, rows: rest });
      },
    });
  }, [readString, reportMonthly]);

  const handleSubmitDisalbeColumnSelect = useCallback((values: any) => {
    setDisableColumns(values.disableColumns);
  }, []);

  const handleSubmitRowHeightSetting = useCallback((values: any) => {
    setRowHeight(values.rowHeight);
  }, []);

  const tableRows = useMemo(() => {
    return data.rows.map((row) => {
      return row.map((column) => {
        return {
          type: "TEXT",
          value: column,
        } as ColumnData;
      });
    });
  }, [data]);

  const tableFooterRow = useMemo(() => {
    return [
      { type: "TEXT", value: "合計" },
      {
        type: "TEXT",
        value: reportMonthly.aggregated_report.unique_user as string,
      },
      {
        type: "TEXT",
        value: reportMonthly.aggregated_report.conversion_user as string,
      },
      {
        type: "TEXT",
        value: reportMonthly.aggregated_report.conversion_user_rate as string,
      },
      {
        type: "TEXT",
        value: reportMonthly.aggregated_report.lp_drop_rate as string,
      },
      {
        type: "TEXT",
        value: reportMonthly.aggregated_report.lp_scroll_rate as string,
      },
      {
        type: "TEXT",
        value: reportMonthly.aggregated_report.lp_average_stay_time as string,
      },
      {
        type: "TEXT",
        value: reportMonthly.aggregated_report.form_drop_rate as string,
      },
      { type: "TEXT", value: reportMonthly.aggregated_report.mobile as string },
      { type: "TEXT", value: reportMonthly.aggregated_report.tablet as string },
      { type: "TEXT", value: reportMonthly.aggregated_report.pc as string },
    ] as ColumnData[];
  }, [reportMonthly]);

  const csvFooterRow = useMemo(() => {
    return [
      "合計",
      reportMonthly.aggregated_report.unique_user.replaceAll(",", ""),
      reportMonthly.aggregated_report.conversion_user.replaceAll(",", ""),
      reportMonthly.aggregated_report.conversion_user_rate,
      reportMonthly.aggregated_report.lp_drop_rate,
      reportMonthly.aggregated_report.lp_scroll_rate,
      reportMonthly.aggregated_report.lp_average_stay_time,
      reportMonthly.aggregated_report.form_drop_rate,
      reportMonthly.aggregated_report.mobile,
      reportMonthly.aggregated_report.tablet,
      reportMonthly.aggregated_report.pc,
    ].join(",");
  }, [reportMonthly]);

  return (
    <>
      <Flex justifyContent="flex-end" mb="24px" mt={6}>
        <RowHeightSettingButton
          formValues={{ rowHeight }}
          onSubmit={handleSubmitRowHeightSetting}
        />
        <DisableColumnSelectButton
          formValues={{ disableColumns }}
          onSubmit={handleSubmitDisalbeColumnSelect}
          options={disableColumnOptions}
        />
        <CSVDownloadLink
          name={"月別レポート"}
          data={reportMonthly.summary_report + csvFooterRow}
          filteredColumns={disableColumns}
        />
      </Flex>
      <Table>
        <ReportTableHeaderRow
          disableColumns={disableColumns}
          onSortClick={onSortClick}
          headerOptions={headerOptionsForMonthlyReport}
        />
        {tableRows.map((row, i) => (
          <ReportTableDataRow
            key={i}
            row={row}
            disableColumns={disableColumns}
          />
        ))}
        <ReportTableDataRow
          disableColumns={disableColumns}
          row={tableFooterRow}
          rowHeight={`${rowHeight}px`}
        />
      </Table>
    </>
  );
};
